import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { priceChecksApi } from '../services/api';
import Navbar from '../components/Navbar';

const PriceCheckDetail = () => {
  const { id } = useParams();
  const [check, setCheck] = useState(null);
  const [results, setResults] = useState({ data: [], page: 1, pages: 1, total: 0 });
  const [error, setError] = useState('');

  const loadCheck = useCallback(async () => {
    try {
      const res = await priceChecksApi.getAll();
      const found = res.data.find(c => String(c.id) === String(id));
      if (found) setCheck(found);
    } catch (err) {
      setError('Failed to load check details');
    }
  }, [id]);

  const loadResults = useCallback(async (page) => {
    try {
      const res = await priceChecksApi.getResults(id, page);
      setResults(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load results');
    }
  }, [id]);

  useEffect(() => {
    loadCheck();
    loadResults(1);
  }, [loadCheck, loadResults]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/price-checks" className="text-sm text-primary-600 hover:text-primary-700 mb-4 inline-block">
          &larr; Back to Price Checks
        </Link>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {check && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">{check.name}</h2>
            <a
              href={check.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary-600 hover:underline break-all"
            >
              {check.url}
            </a>
            <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-400">
              <span>Last run: {check.last_run_time ? new Date(check.last_run_time).toLocaleString() : 'Never'}</span>
              <span>Next run: {check.next_run_time ? new Date(check.next_run_time).toLocaleString() : '—'}</span>
              {check.last_price != null && (
                <span className="text-green-600 font-semibold">{check.last_price}</span>
              )}
              {check.last_in_stock != null && (
                <span className={check.last_in_stock ? 'text-green-600' : 'text-red-500'}>
                  {check.last_in_stock ? 'In Stock' : 'Out of Stock'}
                </span>
              )}
            </div>
            {check.last_error && (
              <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
                Last run did not update the price: {check.last_error}
              </div>
            )}
          </div>
        )}

        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Results{results.total > 0 && <span className="text-sm font-normal text-gray-400 ml-1">({results.total})</span>}
        </h3>

        {results.data.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">No results yet.</div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.data.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(r.run_time).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700 max-w-xs truncate">
                        {r.item_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-900 whitespace-nowrap">
                        {r.price != null ? `${r.currency ? r.currency + ' ' : ''}${r.price}` : '—'}
                        {r.ok === false && (
                          <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                            Failed
                          </span>
                        )}
                        {r.ok !== false && r.flagged && (
                          <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                            Unconfirmed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs max-w-[10rem] truncate">
                        {r.source_url ? (
                          <a
                            href={r.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`hover:underline ${r.confidence === 'low' ? 'text-amber-600' : 'text-primary-600'}`}
                            title={`${r.source_url}${r.confidence ? ` (${r.confidence} confidence)` : ''}`}
                          >
                            {(() => { try { return new URL(r.source_url).hostname.replace(/^www\./, ''); } catch { return r.source_url; } })()}
                          </a>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {r.in_stock == null ? '—' : (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                            r.in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {r.in_stock ? 'In Stock' : 'Out'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-sm">
                        {r.note || '—'}
                        {r.flag_reason && (
                          <div className="text-amber-600 mt-0.5">{r.flag_reason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {r.price_changed && <span className="text-orange-500 font-medium mr-1">Price</span>}
                        {r.stock_changed && <span className="text-blue-500 font-medium">Stock</span>}
                        {!r.price_changed && !r.stock_changed && <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {results.pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: results.pages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => loadResults(p)}
                    className={`px-3 py-1 text-sm rounded border ${
                      p === results.page
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PriceCheckDetail;
